/**
 * 连续动作清单（P2a）的前端类型与校验。
 * 字段照 I 冻结的 F0（docs/coordination/MOTION-MANIFEST-F0-I-2026-10-01.md）第 1、2 节，播放语义照第 3 节：
 * 读口 GET /pets/{pet_id}/motion?purpose=home 回一套的头部加 clips，只给生效且就绪的段。
 *
 * 前端再逐段核一遍：
 * - 哪段不合格就当它没来（停在稳定姿势），不连累别的段；
 * - 同一套合同里，同一个 clip_key 已经在用的好段，不被新回包里坏掉的那段顶掉；头部任一项变了，旧的一整套丢掉（mergeMotionSet，修订 D4）；
 * - 头部形状不对、指纹版本不认识、或对不上这只宠物此刻生效的身份（宠物、身份参考版本、中性站姿指纹、身体版本），整套不用，调用方照旧画静态姿势（修订 D1、D3）。
 * 播放器的身份（signature，React key）由身份字段加各段指纹拼成，身份一变就重建（修订 D2）。
 */
import type { CharacterSet } from "@/shared/contracts";
import { MANIFEST_DIGEST_VERSION, isPrintableAscii, manifestDigest } from "./digest";

/** 状态：居家 STAND、SIT、LIE；贪吃 MOVE、END（F0 第 4 节） */
export type MotionStateId = string;

export interface MotionBox {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

export interface MotionFrame {
  index: number;
  duration_ms: number;
  url: string;
  content_sha256: string;
  /** 主体范围（像素，画布坐标） */
  content_box: MotionBox;
  /** 实测落地点（画布比例） */
  anchor: { x: number; y: number; measured: boolean };
  has_alpha: boolean;
  opaque_ratio: number;
  /** 球的附着点（画布比例），篮球用；null＝这一帧球不在爪里（F0 修订 B4，只增；居家、贪吃可以不给） */
  hold_point?: { x: number; y: number } | null;
  /** 身体是否着地；离地帧（篮球 shoot、land 里）为 false，锚点只在两帧都着地时核（B4） */
  grounded?: boolean;
}

export interface MotionEventMark {
  frame: number;
  name: string;
}

/**
 * 一段：loop 为真是循环段（同一状态的待机、走动）；不循环且起止不同是过渡段；
 * 不循环而起止相同是一次性段（游戏里 MOVE → MOVE 的 eat、blocked、hit），只能由 cut 当场切进去，不参与找路。
 */
export interface MotionClip {
  clip_key: string;
  from_state: MotionStateId;
  to_state: MotionStateId;
  loop: boolean;
  facing: string;
  /** 这段缺帧或加载失败时落到哪个稳定姿势（只能是起点或终点） */
  fallback_state: MotionStateId;
  events: MotionEventMark[];
  /** 可以离开这段的帧（非规则事件的切换用）：循环段、一次性段认声明的帧；过渡段只许首帧（还没动，可以取消）与尾帧；终局段只许尾帧（F0 修订 C1） */
  interrupt_frames: number[];
  frames: MotionFrame[];
  /** 同一 clip_key 单调递增；前端据此认出“换了一版”，重挂播放器 */
  revision: number;
  /** 整段清单指纹：按帧序拼每帧 sha256 与 duration_ms 再取 sha256 */
  manifest_sha256: string;
  /** 这一段自己的站位（贪吃按朝向分组，F0 修订 H2）；其余用途为 null，以头部 anchor 为准。不进规范式；播放照旧用帧上的锚点 */
  anchor?: { x: number; y: number; measured: boolean } | null;
}

export interface MotionSet {
  pet_id: string;
  purpose: string;
  /** 指纹规范的版本，本版 "motion-manifest/1"；前端不认识的版本整套不用（修订 D1） */
  manifest_digest_version: string;
  /** 身份参考的版本；和生效形象的 reference_version 对不上整套不用 */
  reference_version: number;
  /** 冻结的身份参考指纹：等于生效形象中性站姿（neutral_full）那张资产的 content_sha256（修订 D3） */
  identity_sha256: string;
  /** 动作的画风版本（motion-key-v1 一族）；只要求同一套内一致，不和形象的 style_version 比（修订 D3 更正） */
  style_version: string;
  /** 身体投影版本；调用方知道时才比 */
  body_revision: number | null;
  canvas: { width: number; height: number };
  anchor_rule: string;
  /** 全套统一的落地锚点（画布比例），必须量过 */
  anchor: { x: number; y: number; measured: boolean };
  mirror_allowed: boolean;
  generation_method: "keyframe_inbetween" | "video_extract" | "fixture";
  /** 段目录的版本（clip-catalog-1，F0 修订 H5）；不进规范式，但目录换了版本就不算同一套 */
  clip_catalog_version?: string | null;
  clips: MotionClip[];
}

const SHA256 = /^[0-9a-f]{64}$/;
const finite = (value: unknown): value is number => typeof value === "number" && Number.isFinite(value);

/** 一帧能不能放进场景：透明可用、主体范围在画布内、落地点量过且在画布内、有指纹与地址、时长为正。 */
export function frameProblems(frame: MotionFrame, canvas: MotionSet["canvas"], where: string): string[] {
  const out: string[] = [];
  const box = frame.content_box;
  if (!frame.url) out.push(`${where}: 没有地址`);
  if (!SHA256.test(frame.content_sha256 ?? "")) out.push(`${where}: 指纹不是 64 位十六进制`);
  if (!frame.has_alpha) out.push(`${where}: 透明通道不可用`);
  if (!(finite(frame.opaque_ratio) && frame.opaque_ratio > 0 && frame.opaque_ratio <= 1)) out.push(`${where}: 不透明比例不对`);
  if (!(finite(frame.duration_ms) && frame.duration_ms > 0)) out.push(`${where}: 时长不对`);
  if (!box || !(box.left >= 0 && box.top >= 0 && box.right > box.left && box.bottom > box.top && box.right <= canvas.width && box.bottom <= canvas.height)) out.push(`${where}: 主体范围不对`);
  const anchor = frame.anchor;
  if (!anchor || !(anchor.x >= 0 && anchor.x <= 1 && anchor.y >= 0 && anchor.y <= 1)) out.push(`${where}: 落地点不在画布内`);
  else if (!anchor.measured) out.push(`${where}: 落地点没量过`);
  const hold = frame.hold_point;
  if (hold && box && !(hold.x * canvas.width >= box.left && hold.x * canvas.width <= box.right && hold.y * canvas.height >= box.top && hold.y * canvas.height <= box.bottom)) out.push(`${where}: 球的附着点不在主体范围内`);
  if (frame.grounded !== undefined && typeof frame.grounded !== "boolean") out.push(`${where}: 着地标记不对`);
  return out;
}

/** 一段的问题；空数组才算能播。 */
export function clipProblems(clip: MotionClip, canvas: MotionSet["canvas"]): string[] {
  const where = `片段 ${clip.clip_key}`;
  const out: string[] = [];
  const count = clip.frames?.length ?? 0;
  if (!count) out.push(`${where}: 没有帧`);
  const texts = [clip.clip_key, clip.from_state, clip.to_state, clip.facing, clip.fallback_state, ...(clip.events ?? []).map((e) => e.name)];
  if (!texts.every(isPrintableAscii)) out.push(`${where}: 有不是可打印 ASCII 的字（修订 D1）`);
  if (!(Number.isInteger(clip.revision) && clip.revision >= 1)) out.push(`${where}: 版本号不对`);
  if (!SHA256.test(clip.manifest_sha256 ?? "")) out.push(`${where}: 整段指纹不是 64 位十六进制`);
  if (clip.loop && clip.from_state !== clip.to_state) out.push(`${where}: 循环段的起止必须是同一个状态`);
  if (clip.fallback_state !== clip.from_state && clip.fallback_state !== clip.to_state) out.push(`${where}: 回落状态只能是起点或终点`);
  let last = -1;
  for (const frame of clip.interrupt_frames ?? []) {
    if (!(Number.isInteger(frame) && frame > last && frame < count)) out.push(`${where}: 可切换帧 ${frame} 不对（要从小到大、在帧数以内）`);
    else if (!clip.loop && clip.from_state !== clip.to_state && frame !== 0 && frame !== count - 1) out.push(`${where}: 过渡段只能在首帧或尾帧切换（第 ${frame} 帧不行）`);
    last = frame;
  }
  for (const mark of clip.events ?? []) {
    if (!(Number.isInteger(mark.frame) && mark.frame >= 0 && mark.frame < count) || !mark.name) out.push(`${where}: 事件标记 ${mark.name || "（没名字）"}@${mark.frame} 不对`);
  }
  clip.frames?.forEach((frame, index) => {
    if (frame.index !== index) out.push(`${where} 第 ${index} 帧: 帧号是 ${frame.index}`);
    out.push(...frameProblems(frame, canvas, `${where} 第 ${index} 帧`));
  });
  return out;
}

/** 头部的问题：有问题整套不用。 */
export function headerProblems(set: MotionSet | null | undefined): string[] {
  if (!set) return ["没有动作清单"];
  const out: string[] = [];
  if (!(set.canvas && finite(set.canvas.width) && finite(set.canvas.height) && set.canvas.width > 0 && set.canvas.height > 0)) out.push("画布尺寸不对");
  if (set.manifest_digest_version !== MANIFEST_DIGEST_VERSION) out.push("清单指纹版本不认识");
  if (!(Number.isInteger(set.reference_version) && set.reference_version >= 1)) out.push("身份参考版本不对");
  if (!SHA256.test(set.identity_sha256 ?? "")) out.push("身份参考指纹不是 64 位小写十六进制");
  if (![set.pet_id, set.purpose, set.style_version, set.anchor_rule, set.generation_method].every(isPrintableAscii)) out.push("头部有不是可打印 ASCII 的字");
  const anchor = set.anchor;
  if (!anchor || !(anchor.x >= 0 && anchor.x <= 1 && anchor.y >= 0 && anchor.y <= 1) || !anchor.measured) out.push("落地锚点不对或没量过");
  if (!Array.isArray(set.clips)) out.push("没有片段列表");
  return out;
}

/** 调用方此刻认的身份：生效那一套形象（要它的宠物、身份参考版本与中性站姿资产），以及（知道的话）身体版本。 */
export interface MotionIdentity {
  set: Pick<CharacterSet, "pet_id" | "reference_version" | "assets">;
  /** undefined＝调用方不知道，不比；null 与数字照实比 */
  bodyRevision?: number | null;
}

/**
 * 这套动作是不是这只宠物此刻生效的身份（修订 D3）：宠物、身份参考版本要对上，身份参考指纹要等于生效形象中性站姿那张资产的 content_sha256；
 * 身体版本调用方给了才比。画风不比（动作与形象是两套画风家族）。版本号相等只证明声明的是同一身份，不证明画面像同一只。
 */
export function matchesIdentity(set: MotionSet, identity: MotionIdentity): boolean {
  const active = identity.set;
  const neutral = active.assets?.find((asset) => asset.pose === "neutral_full");
  return (
    set.pet_id === active.pet_id &&
    set.reference_version === active.reference_version &&
    !!neutral && set.identity_sha256 === neutral.content_sha256 &&
    (identity.bodyRevision === undefined || set.body_revision === identity.bodyRevision)
  );
}

/**
 * 同一套合同（修订 D4、H1、H5）：宠物、用途、身份、身体、画风、画布、锚点规则与锚点、生成方式、能不能镜像、段目录版本全都相同。
 * 生成方式或镜像不同就是两套，不混补（H1）；目录版本没给与 null 当成一样。
 * 指纹版本与锚点「量过」不另比：两边头部都合法时只能是 motion-manifest/1、只能是量过，恒等；以后认第二个指纹版本时把它加回来并补用例。
 */
function sameHeader(a: MotionSet, b: MotionSet): boolean {
  return (
    a.pet_id === b.pet_id &&
    a.purpose === b.purpose &&
    a.reference_version === b.reference_version &&
    a.identity_sha256 === b.identity_sha256 &&
    a.style_version === b.style_version &&
    a.body_revision === b.body_revision &&
    a.canvas.width === b.canvas.width &&
    a.canvas.height === b.canvas.height &&
    a.anchor_rule === b.anchor_rule &&
    a.anchor.x === b.anchor.x &&
    a.anchor.y === b.anchor.y &&
    a.generation_method === b.generation_method &&
    a.mirror_allowed === b.mirror_allowed &&
    (a.clip_catalog_version ?? null) === (b.clip_catalog_version ?? null)
  );
}

/**
 * 新回包到了：同一套（头部一样）里，某个 clip_key 的新段坏了而手上那段是好的，就留着手上那段——好段不被坏段顶掉。
 * 新回包里没有的段（撤下了）不留；头部变了（换了身份、身体、画布）旧段一律不带。
 */
export function mergeMotionSet(previous: MotionSet | null, incoming: MotionSet | null | undefined): MotionSet | null {
  if (!incoming) return null;
  if (!previous || headerProblems(previous).length || headerProblems(incoming).length || !sameHeader(previous, incoming)) return incoming;
  const good = new Map(previous.clips.filter((clip) => clipProblems(clip, previous.canvas).length === 0).map((clip) => [clip.clip_key, clip]));
  return { ...incoming, clips: incoming.clips.map((clip) => (clipProblems(clip, incoming.canvas).length === 0 ? clip : good.get(clip.clip_key) ?? clip)) };
}

/** 能播的一套：只含合格的段，外加每个状态的稳定姿势。 */
export interface PreparedMotion {
  set: MotionSet;
  clips: MotionClip[];
  /** 每个状态的稳定姿势：它的循环段首帧，没有循环段就取进它的过渡段尾帧，再没有就取从它出发的过渡段首帧 */
  stable: ReadonlyMap<MotionStateId, MotionFrame>;
  /** 按哪个状态的主体高度定比例：有 STAND 用 STAND（和静态姿态图按中性站姿定比例同一条规矩） */
  referenceState: MotionStateId;
  /**
   * 播放器的身份（React key，修订 D2）：pet_id、purpose、指纹版本、reference_version、identity_sha256，加各段的 manifest_sha256，
   * 再加本地按规范式算的语义摘要——后端指纹为准，本地摘要只是兜底：哪怕回包的指纹没跟着改（比如只改了事件名），播放器也照样重建。
   */
  signature: string;
  dropped: { clip_key: string; problems: string[] }[];
}

function stableFrameOf(clips: MotionClip[], state: MotionStateId): MotionFrame | null {
  const loop = clips.find((clip) => clip.loop && clip.from_state === state);
  if (loop) return loop.frames[0];
  const into = clips.find((clip) => !clip.loop && clip.to_state === state);
  if (into) return into.frames[into.frames.length - 1];
  const out = clips.find((clip) => !clip.loop && clip.from_state === state);
  return out ? out.frames[0] : null;
}

/** 逐段校验、去掉不合格的段；头部坏了、身份对不上、一段能播的都没有，返回 null（调用方画静态姿势）。 */
export function prepareMotion(set: MotionSet | null | undefined, identity: MotionIdentity | null): PreparedMotion | null {
  if (!set || !identity || headerProblems(set).length || !matchesIdentity(set, identity)) return null;
  const clips: MotionClip[] = [];
  const dropped: PreparedMotion["dropped"] = [];
  const localDigests = new Map<MotionClip, string>();
  const keys = new Set<string>();
  // 终局段：进一个没有任何段出发的状态（END），只许在尾帧切换（F0 修订 C1）
  const leaving = new Set(set.clips.map((clip) => clip.from_state));
  for (const clip of set.clips) {
    const problems = clipProblems(clip, set.canvas);
    const last = (clip.frames?.length ?? 0) - 1;
    if (!clip.loop && clip.from_state !== clip.to_state && !leaving.has(clip.to_state) && (clip.interrupt_frames ?? []).some((frame) => frame !== last)) {
      problems.push(`片段 ${clip.clip_key}: 终局段只能在尾帧切换`);
    }
    // 只要 clip_key 不重复：同一段路可以有几段（F0 里 end_win、end_lose 都是 MOVE → END），找路时取清单里靠前的
    if (keys.has(clip.clip_key)) problems.push(`片段 ${clip.clip_key} 重复`);
    if (!problems.length) {
      try {
        localDigests.set(clip, manifestDigest(set, clip));
      } catch (error) {
        problems.push(`片段 ${clip.clip_key}: 规范式算不出来（${error instanceof Error ? error.message : "未知"}）`);
      }
    }
    if (problems.length) {
      dropped.push({ clip_key: clip.clip_key, problems });
      continue;
    }
    keys.add(clip.clip_key);
    clips.push(clip);
  }
  const stable = new Map<MotionStateId, MotionFrame>();
  for (const clip of clips) {
    for (const state of [clip.from_state, clip.to_state]) {
      if (!stable.has(state)) stable.set(state, stableFrameOf(clips, state)!);
    }
  }
  if (!stable.size) return null;
  const referenceState = stable.has("STAND") ? "STAND" : clips[0].from_state;
  const head = [set.pet_id, set.purpose, set.manifest_digest_version, set.reference_version, set.identity_sha256].join("|");
  const signature = `${head}||${clips.map((clip) => `${clip.clip_key}:${clip.manifest_sha256}:${localDigests.get(clip)}`).join("|")}`;
  return { set: { ...set, clips }, clips, stable, referenceState, signature, dropped };
}
